// MATERIAL UI
import { Grid, Typography, FormControl, Button, Alert } from "@mui/material";

// STYLES
import { PaymentStyles } from "./Payment.styles";

// COMPONENTS
import { FormInput, InputStyleType } from "src/components/input/FormInput";
import { AutoAddress } from "src/components/input/AutoAddress";
import { ButtonContainer } from "./ButtonContainer";
import { AlertCard, AlertStyleType } from "src/components/alerts/AlertCard";

// TYPES
import { FormSelect, SelectType } from "src/components/wallets/Modal/Withdraw/FormSelect";

// REDUX
import { useDispatch } from "react-redux";
import { setStep, useBeneficiarySlice } from "src/store/features/beneficiary.slice";
import { useUserSlice } from "src/store/features/user.slice";

// CONSTANTS
import { AVAILABLE_CURRENCIES } from "./constants/currencies";

// HOOKS
import { useBankForm } from "src/utils/hooks/banking/useBankForm";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

export const BankForm = (): JSX.Element => {
  const classes = PaymentStyles();
  const dispatch = useDispatch();

  /*********
   * HOOKS *
   *********/
  const { is2FAEnabled } = useUserSlice();

  const {
    currency,
    beneficiaryName,
    beneficiaryAddress,
    bankName,
    bankAddress,
    swiftCode,
    sortCodeOrBsb,
    ibanAccNumber,
    transitNumber,
    institutionNumber,
    abaRoutingNumber,
    identityType,
    accountType,
  } = useBeneficiarySlice();

  const {
    formErrors,
    isBeneficiaryAddressEntered,
    isBankAddressEntered,
    isBankDetailsPresent,
    isBankDetailsLoading,
    handleCurrencySelection,
    handleAddressInput,
    handleAddressFields,
    handleConfirmBankDetails,
    handleBeneficiaryName,
    handleBankName,
    handleIbanAccNumber,
    handleSwiftCode,
    handleInstitutionNumber,
    handleAbaRoutingNumber,
    handleSortCodeOrBsb,
    handleTransitNumber,
    handleAddressSearchValue,
    handleIdentityType,
    handleAccountType,
  } = useBankForm();

  /************
   * HANDLERS *
   ************/
  const handleGoBack = () => {
    dispatch(setStep("ViewDetails"));
    trackEvent("User clicked Go Back", {}, "Payment");
  };

  /**************
   * VAR HELPERS
   **************/
  const isUsdOrGbp = currency.value === "USD" || currency.value === "GBP";
  const isGbpOrEur = currency.value === "GBP" || currency.value === "EUR";
  const isGbpOrAud = currency.value === "GBP" || currency.value === "AUD";
  const isGbp = currency.value === "GBP";
  const isAud = currency.value === "AUD";
  const isUsd = currency.value === "USD";

  return (
    <>
      <Grid item width="100%">
        {!is2FAEnabled && (
          <AlertCard
            titleText="Action Required"
            subText="It looks like you don’t have 2FA enabled, click"
            link="/settings/security"
            linkText="here"
          />
        )}
        {isBankDetailsPresent && (
          <AlertCard
            styleType={AlertStyleType.ERROR}
            titleText="Please submit a different bank account"
            subText={`Your bank account ending with the account number ${isBankDetailsPresent.trailingAccNumberDigits} has already been added to your account. If you need any help, feel free to contact support.`}
          />
        )}
        <Typography className={classes.bankManagementSubHeader} style={{ textAlign: "center" }}>
          Add new Bank Account
        </Typography>
      </Grid>
      <Grid item width="100%" paddingTop={"0 !important"} paddingBottom="24px">
        <FormControl fullWidth>
          <FormSelect
            fullWidth
            title="Currency"
            labelId="currency-label"
            id="currency"
            placeholder="Currency"
            value={currency.value}
            onChange={handleCurrencySelection}
            type={SelectType.IMAGE}
            options={
              AVAILABLE_CURRENCIES?.map((currency) => ({
                value: currency.value,
                image: currency.image,
              })) || []
            }
          />
        </FormControl>
        {isAud && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            AUD withdrawals are to domestic Australian bank accounts only
          </Alert>
        )}
      </Grid>
      <Grid item width="100%" paddingBottom="24px">
        <FormInput
          title="Account Holder Name"
          styleType={formErrors.beneficiaryName ? InputStyleType.FORM_ERROR : InputStyleType.FORM}
          onChange={(e) => handleBeneficiaryName(e)}
          value={beneficiaryName}
          name="beneficiaryName"
          placeholder="Enter Your Account Name here"
          errorMessage={formErrors.beneficiaryName}
        />
      </Grid>
      <Grid item width="100%" paddingBottom="24px">
        <FormControl fullWidth>
          <AutoAddress
            title="Account Holder Address"
            placeholder="Search Address"
            name="beneficiaryAddressSearch"
            onChange={(e) => handleAddressSearchValue(e, "beneficiaryAddressSearch")}
            onSubmit={(data) => handleAddressFields(data, "beneficiary")}
            errorMessage={formErrors.beneficiaryAddressSearch}
          />
          <Grid
            container
            spacing={2}
            display="flex"
            justifyContent="space-between"
            paddingTop="15px">
            {isBeneficiaryAddressEntered && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <FormInput
                    name="streetAddress"
                    label="Street Address"
                    styleType={
                      formErrors.beneficiaryStreetAddress
                        ? InputStyleType.FORM_ERROR
                        : InputStyleType.FORM
                    }
                    onChange={(e) => handleAddressInput(e, "beneficiary")}
                    value={beneficiaryAddress?.streetAddress}
                    placeholder="Enter Street Address"
                    errorMessage={formErrors.beneficiaryStreetAddress}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormInput
                    name="city"
                    label="City"
                    styleType={
                      formErrors.beneficiaryCity ? InputStyleType.FORM_ERROR : InputStyleType.FORM
                    }
                    onChange={(e) => handleAddressInput(e, "beneficiary")}
                    value={beneficiaryAddress?.city}
                    placeholder="Enter City"
                    errorMessage={formErrors.beneficiaryCity}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormInput
                    name="unit"
                    label="(Optional) Unit/Apartment no."
                    styleType={InputStyleType.FORM}
                    onChange={(e) => handleAddressInput(e, "beneficiary")}
                    value={beneficiaryAddress?.unit}
                    placeholder="Enter Unit/Apartment Number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormInput
                    name="country"
                    label="Country"
                    styleType={
                      formErrors.beneficiaryCountry
                        ? InputStyleType.FORM_ERROR
                        : InputStyleType.FORM
                    }
                    onChange={(e) => handleAddressInput(e, "beneficiary")}
                    value={beneficiaryAddress?.country}
                    placeholder="Enter Country"
                    errorMessage={formErrors.beneficiaryCountry}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormInput
                    name="state"
                    label="State"
                    styleType={
                      formErrors.beneficiaryState ? InputStyleType.FORM_ERROR : InputStyleType.FORM
                    }
                    onChange={(e) => handleAddressInput(e, "beneficiary")}
                    value={beneficiaryAddress?.state}
                    placeholder="Enter State"
                    errorMessage={formErrors.beneficiaryState}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormInput
                    name="postcode"
                    label="Postcode"
                    styleType={
                      formErrors.beneficiaryPostcode
                        ? InputStyleType.FORM_ERROR
                        : InputStyleType.FORM
                    }
                    onChange={(e) => handleAddressInput(e, "beneficiary")}
                    value={beneficiaryAddress?.postcode}
                    placeholder="Enter Postcode"
                    errorMessage={formErrors.beneficiaryPostcode}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </FormControl>
      </Grid>
      {isUsd && (
        <Grid
          container
          item
          justifyContent="space-between"
          flexDirection="row"
          paddingBottom="24px"
          rowSpacing={3}
          columnSpacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <FormSelect
                title="Identity Type"
                labelId="identityType-label"
                id="identityType"
                placeholder="Identity Type"
                value={identityType}
                onChange={handleIdentityType}
                options={[
                  { value: "Individual", displayName: "Individual" },
                  { value: "Business", displayName: "Business" },
                ]}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <FormSelect
                title="Account Type"
                labelId="accountType-label"
                id="accountType"
                placeholder="Account Type"
                value={accountType}
                onChange={handleAccountType}
                options={[
                  { value: "CACC", displayName: "Checking" },
                  { value: "SVGS", displayName: "Savings" },
                ]}
              />
            </FormControl>
          </Grid>
        </Grid>
      )}

      <Grid item width="100%" paddingBottom="24px">
        <FormInput
          title="Bank Name"
          styleType={formErrors.bankName ? InputStyleType.FORM_ERROR : InputStyleType.FORM}
          onChange={(e) => handleBankName(e)}
          value={bankName}
          placeholder="Enter Bank Name"
          name="bankName"
          errorMessage={formErrors.bankName}
        />
      </Grid>
      <Grid item width="100%" paddingBottom="24px">
        <FormControl fullWidth>
          <AutoAddress
            title="Bank Address"
            placeholder="Search Address"
            name="bankAddressSearch"
            onChange={(e) => handleAddressSearchValue(e, "bankAddressSearch")}
            onSubmit={(data) => handleAddressFields(data, "bank")}
            errorMessage={formErrors.bankAddressSearch}
          />
          <Grid
            container
            spacing={2}
            display="flex"
            justifyContent="space-between"
            paddingTop="15px">
            {isBankAddressEntered && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <FormInput
                    label="Street Address"
                    onChange={(e) => handleAddressInput(e, "bank")}
                    name="streetAddress"
                    value={bankAddress?.streetAddress}
                    placeholder="Enter Street Address"
                    styleType={
                      formErrors.bankStreet ? InputStyleType.FORM_ERROR : InputStyleType.FORM
                    }
                    errorMessage={formErrors.bankStreet}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormInput
                    label="City"
                    styleType={
                      formErrors.bankCity ? InputStyleType.FORM_ERROR : InputStyleType.FORM
                    }
                    name="city"
                    onChange={(e) => handleAddressInput(e, "bank")}
                    value={bankAddress?.city}
                    placeholder="Enter City"
                    errorMessage={formErrors.bankCity}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormInput
                    label="(Optional) Unit/Apartment no."
                    styleType={InputStyleType.FORM}
                    name="unit"
                    onChange={(e) => handleAddressInput(e, "bank")}
                    value={bankAddress?.unit}
                    placeholder="Enter Unit/Apartment Number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormInput
                    label="Country"
                    styleType={
                      formErrors.bankCountry ? InputStyleType.FORM_ERROR : InputStyleType.FORM
                    }
                    onChange={(e) => handleAddressInput(e, "bank")}
                    name="country"
                    value={bankAddress?.country}
                    placeholder="Enter Country"
                    errorMessage={formErrors.bankCountry}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormInput
                    label="State"
                    styleType={
                      formErrors.bankState ? InputStyleType.FORM_ERROR : InputStyleType.FORM
                    }
                    name="state"
                    onChange={(e) => handleAddressInput(e, "bank")}
                    value={bankAddress?.state}
                    placeholder="Enter State"
                    errorMessage={formErrors.bankState}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormInput
                    label="Postcode"
                    styleType={
                      formErrors.bankPostcode ? InputStyleType.FORM_ERROR : InputStyleType.FORM
                    }
                    name="postcode"
                    onChange={(e) => handleAddressInput(e, "bank")}
                    value={bankAddress?.postcode}
                    placeholder="Enter Postcode"
                    errorMessage={formErrors.bankPostcode}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </FormControl>
        <Grid
          container
          item
          justifyContent="space-between"
          flexDirection="row"
          padding="24px 0"
          rowSpacing={3}
          columnSpacing={3}>
          {currency.value === "USD" && (
            <Grid item xs={12} md={4}>
              <FormInput
                title="ABA Routing Number"
                styleType={
                  formErrors.abaRoutingNumber ? InputStyleType.FORM_ERROR : InputStyleType.FORM
                }
                name="abaRoutingNumber"
                type="number"
                onChange={(e) => handleAbaRoutingNumber(e)}
                value={abaRoutingNumber}
                placeholder="Enter ABA Routing Number"
                errorMessage={formErrors.abaRoutingNumber}
              />
            </Grid>
          )}
          {isGbpOrAud && (
            <Grid item xs={12} md={isAud ? 6 : 4}>
              <FormInput
                title={currency.value === "AUD" ? "BSB" : "Sort Code"}
                styleType={
                  formErrors.sortCodeOrBsb ? InputStyleType.FORM_ERROR : InputStyleType.FORM
                }
                type="number"
                name="sortCodeOrBsb"
                onChange={(e) => handleSortCodeOrBsb(e)}
                value={sortCodeOrBsb}
                placeholder={`Enter ${currency.value === "AUD" ? "BSB" : "Sort Code"}`}
                errorMessage={formErrors.sortCodeOrBsb}
              />
            </Grid>
          )}
          <Grid item xs={12} md={isUsdOrGbp || isGbp ? 4 : 6}>
            <FormInput
              title={isGbpOrEur ? "IBAN" : "Account Number"}
              styleType={formErrors.ibanAccNumber ? InputStyleType.FORM_ERROR : InputStyleType.FORM}
              name="ibanAccNumber"
              onChange={(e) => handleIbanAccNumber(e)}
              value={ibanAccNumber}
              placeholder={`Enter ${isGbpOrEur ? "IBAN" : "Account"} Number here`}
              errorMessage={formErrors.ibanAccNumber}
            />
          </Grid>

          {currency.value === "CAD" && (
            <Grid item xs={12} sm={12} md={6}>
              <FormInput
                title="Institution Number"
                styleType={
                  formErrors.institutionNumber ? InputStyleType.FORM_ERROR : InputStyleType.FORM
                }
                name="institutionNumber"
                type="number"
                onChange={(e) => handleInstitutionNumber(e)}
                value={institutionNumber}
                placeholder="Enter Institution Number"
                errorMessage={formErrors.institutionNumber}
              />
            </Grid>
          )}
          {currency.value === "CAD" && (
            <Grid item xs={12} sm={12} md={6}>
              <FormInput
                title="Transit Number"
                styleType={
                  formErrors.transitNumber ? InputStyleType.FORM_ERROR : InputStyleType.FORM
                }
                name="transitNumber"
                type="number"
                onChange={(e) => handleTransitNumber(e)}
                value={transitNumber}
                placeholder="Enter Transit Number"
                errorMessage={formErrors.transitNumber}
              />
            </Grid>
          )}
          {!isAud && (
            <Grid item xs={12} md={isUsdOrGbp ? 4 : 6}>
              <FormInput
                title="Swift Code"
                styleType={formErrors.swiftCode ? InputStyleType.FORM_ERROR : InputStyleType.FORM}
                type="text"
                name="swiftCode"
                onChange={(e) => handleSwiftCode(e)}
                value={swiftCode}
                placeholder="Enter Swift Code here"
                errorMessage={formErrors.swiftCode}
              />
            </Grid>
          )}
        </Grid>
        <ButtonContainer>
          <Button
            disableRipple
            variant="text"
            onClick={handleGoBack}
            className={classes.secondaryButton}>
            Go Back
          </Button>
          <Button
            disableRipple
            variant="text"
            onClick={handleConfirmBankDetails}
            disabled={isBankDetailsLoading || !is2FAEnabled}
            className={classes.primaryButton}>
            Confirm
          </Button>
        </ButtonContainer>
      </Grid>
    </>
  );
};
